import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import client from "../../../api/client";
import { GlobalContext } from "../../../context";
import { getCurrentUser, logoutHandler, UpdateUserPassword } from "../../../context/actions/auth";
import { payNextInstallment } from "../../../page/Dashboard/SubscriptionsPage";
import { useInstall } from "../../../pwa";
import { currencyFormatter } from "../../../utility";
import { setZone, WAT, ZONES } from "../home/time";
import Icon from "./Icon";
import PageFrame from "./PageFrame";

const TABS = [["profile", "Profile"], ["password", "Password"], ["billing", "Billing"], ["app", "App"]];

/** Keep the saved copy of the profile in step, so a refresh doesn't briefly show old values. */
const storeUser = (user, token, authDispatch) => {
  try {
    localStorage.setItem("userData", JSON.stringify(user));
  } catch {
    // The next /user fetch saves it.
  }
  authDispatch({ type: "SUCCESS", payload: { user, token } });
};

function Profile({ user, token, authDispatch }) {
  const fileRef = useRef(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!user || form) return;
    setForm({
      firstname: user.firstname || "",
      lastname: user.lastname || "",
      telephone: user.telephone || "",
      professional_title: user.professional_title || "",
      company: user.company || "",
      time_zone: user.time_zone || WAT,
    });
  }, [user, form]);

  if (!form) return <div className="skeleton" style={{ height: 320 }} />;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const certName = [form.firstname, form.lastname].map((v) => v.trim()).filter(Boolean).join(" ");
  const initials = `${user.firstname?.[0] ?? ""}${user.lastname?.[0] ?? ""}`.toUpperCase() || "E1";
  const zones = ZONES.some(([tz]) => tz === form.time_zone) ? ZONES : [...ZONES, [form.time_zone, form.time_zone]];

  const save = async (e) => {
    e.preventDefault();
    if (!form.firstname.trim() || !form.lastname.trim()) {
      toast.error("Enter your first and last name. They're printed on your certificates.");
      return;
    }
    setSaving(true);
    try {
      const body = { ...form, firstname: form.firstname.trim(), lastname: form.lastname.trim(), time_zone: form.time_zone === WAT ? null : form.time_zone };
      await client.post("/user/info/update", body);
      storeUser({ ...user, ...body }, token, authDispatch);
      setZone(body.time_zone);
      toast.success("Changes saved");
      getCurrentUser(authDispatch);
    } catch (err) {
      toast.error(err?.response?.data?.message || "We couldn't save your changes. Please try again.");
    }
    setSaving(false);
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await client.post("/user/profile-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      storeUser({ ...user, profile_img: res.data.url }, token, authDispatch);
      toast.success("Photo updated");
    } catch (err) {
      toast.error(err?.response?.data?.message || "We couldn't upload that photo. Try a JPG or PNG.");
    }
    setUploading(false);
  };

  return (
    <form onSubmit={save} style={{ display: "grid", gap: 18 }}>
      <div className="profile-photo">
        <span className="av big">{user.profile_img ? <img src={user.profile_img} alt="" /> : initials}</span>
        <div><strong>Profile photo</strong><small>JPG or PNG, at least 200 × 200 px</small></div>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg" hidden onChange={upload} />
        <button className="cbtn quiet" type="button" onClick={() => fileRef.current?.click()} disabled={uploading}>{uploading ? "Uploading…" : "Change photo"}</button>
      </div>
      <div className="notice">
        <Icon name="info" size={18} />
        <div>
          <strong>Your name appears on your certificates</strong>
          <span>Your first and last name are printed on your certificates exactly as you enter them here. Make sure they match your official ID, with the right spelling and order.</span>
          <span className="cert-name">Name on your certificates: <b>{certName || "—"}</b></span>
        </div>
      </div>
      <div className="form two">
        <label htmlFor="st-first">First name<input id="st-first" value={form.firstname} onChange={set("firstname")} autoComplete="given-name" /></label>
        <label htmlFor="st-last">Last name<input id="st-last" value={form.lastname} onChange={set("lastname")} autoComplete="family-name" /></label>
        <label htmlFor="st-email">Email<input id="st-email" value={user.email || ""} disabled /><small>Contact support to change your email.</small></label>
        <label htmlFor="st-phone">Phone number<input id="st-phone" value={form.telephone} onChange={set("telephone")} autoComplete="tel" /></label>
        <label htmlFor="st-title">Professional title<input id="st-title" value={form.professional_title} onChange={set("professional_title")} placeholder="e.g. Aspiring Data Analyst" /></label>
        <label htmlFor="st-company">Company<input id="st-company" value={form.company} onChange={set("company")} placeholder="Where you work, if anywhere" /></label>
        <label htmlFor="st-tz">Time zone
          <select id="st-tz" value={form.time_zone} onChange={set("time_zone")}>
            {zones.map(([tz, label]) => <option key={tz} value={tz}>{label}</option>)}
          </select>
          <small>Class times are shown in this time zone.</small>
        </label>
      </div>
      <div className="cta-row"><button className="cbtn go" type="submit" disabled={saving}>{saving ? "Saving…" : "Save changes"}</button></div>
    </form>
  );
}

function Password({ authDispatch }) {
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setPw({ ...pw, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    if (!pw.current_password || !pw.new_password) return toast.error("Fill in your current and new password.");
    if (pw.new_password.length < 8) return toast.error("Your new password needs at least 8 characters.");
    if (pw.new_password !== pw.confirm) return toast.error("The new passwords don't match.");
    setSaving(true);
    const ok = await UpdateUserPassword({ current_password: pw.current_password, new_password: pw.new_password }, authDispatch);
    setSaving(false);
    if (ok) {
      toast.success("Password updated");
      setPw({ current_password: "", new_password: "", confirm: "" });
    }
    return undefined;
  };

  return (
    <form onSubmit={save} style={{ display: "grid", gap: 18 }}>
      <div className="form" style={{ maxWidth: 420 }}>
        <label htmlFor="pw-cur">Current password<input id="pw-cur" type="password" value={pw.current_password} onChange={set("current_password")} autoComplete="current-password" /></label>
        <label htmlFor="pw-new">New password<input id="pw-new" type="password" value={pw.new_password} onChange={set("new_password")} autoComplete="new-password" /><small>At least 8 characters.</small></label>
        <label htmlFor="pw-conf">Confirm new password<input id="pw-conf" type="password" value={pw.confirm} onChange={set("confirm")} autoComplete="new-password" /></label>
      </div>
      <div className="cta-row"><button className="cbtn go" type="submit" disabled={saving}>{saving ? "Updating…" : "Update password"}</button></div>
    </form>
  );
}

function Billing({ email }) {
  const navigate = useNavigate();
  const [subs, setSubs] = useState(null);
  const [paying, setPaying] = useState(null);

  useEffect(() => {
    client.get("/subscribers/my-access-status").then((r) => setSubs(r.data?.subscriptions || [])).catch(() => setSubs(false));
  }, []);

  if (subs === null) return <div className="skeleton" style={{ height: 160 }} />;
  if (subs === false) return <p className="course-tag">We couldn't load your payments. Refresh the page to try again.</p>;
  if (!subs.length) return <p className="course-tag">No payments yet. Courses you enroll in appear here with what you've paid.</p>;

  const fmt = (n, sub) => currencyFormatter(n, { currency: sub.currency });
  return (
    <div className="plain">
      <table className="gtable">
        <thead><tr><th>Course</th><th>Payment</th><th>Paid</th></tr></thead>
        <tbody>
          {subs.map((s) => {
            // Same rule as the Subscriptions page: full or grandfathered access counts as paid.
            const full = s.access_tier === "full" || s.access_tier === "grandfathered";
            const left = full ? 0 : Math.max(0, (s.total_price || 0) - (s.amount_paid || 0));
            const plan = full ? "Full access" : s.total_installments ? `${s.installments_paid || 0} of ${s.total_installments} instalments` : left > 0 ? "Part paid" : "Paid in full";
            return (
              <tr key={s.subscriber_uuid}>
                <td>
                  <strong style={{ color: "var(--ink)", fontWeight: 500 }}>{s.course_title}</strong>
                  <small style={{ display: "block", color: "var(--muted)" }}>{s.is_overdue ? "Payment overdue" : left > 0 ? `${fmt(left, s)} left to pay` : "Fully paid"}</small>
                </td>
                <td>
                  <span style={{ color: "var(--muted)" }}>{plan}</span>
                  {s.status === "partial" && s.can_pay_next && (
                    <div style={{ marginTop: 6 }}>
                      <button className="cbtn go" type="button" disabled={paying === s.subscriber_uuid}
                        onClick={() => { setPaying(s.subscriber_uuid); payNextInstallment({ sub: s, email, navigate, onStop: () => setPaying(null) }); }}>
                        {paying === s.subscriber_uuid ? "Opening…" : `Pay instalment ${(s.installments_paid || 0) + 1}${s.amount_per_install ? ` · ${fmt(s.amount_per_install, s)}` : ""}`}
                      </button>
                    </div>
                  )}
                </td>
                <td>{fmt(s.amount_paid || 0, s)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Install the dashboard as an app (PWA, see src/pwa.jsx). */
function AppInstall() {
  const { canInstall, installed, ios, install } = useInstall();

  return (
    <div style={{ display: "grid", gap: 18 }}>
      <div className="profile-photo">
        <img src="/pwa-192.png" alt="" width={56} height={56} style={{ borderRadius: 14 }} />
        <div><strong>E1X Academy app</strong><small>Open your classes, quizzes and grades straight from your home screen or desktop, in their own window.</small></div>
        {installed ? <span className="course-tag"><Icon name="check" size={14} /> Installed</span>
          : canInstall && <button className="cbtn go" type="button" onClick={install}><Icon name="download" size={16} /> Install app</button>}
      </div>
      {installed ? (
        <p className="course-tag">You're using the app on this device. To remove it, uninstall it like any other app.</p>
      ) : ios ? (
        <ol style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 6 }}>
          <li>Open this page in <b>Safari</b>.</li>
          <li>Tap the <b>Share</b> button (the square with an arrow).</li>
          <li>Scroll down and tap <b>Add to Home Screen</b>, then <b>Add</b>.</li>
        </ol>
      ) : !canInstall && (
        <div className="notice">
          <Icon name="info" size={18} />
          <div>
            <strong>Install from your browser menu</strong>
            <span>On Android, open the Chrome menu (⋮) and tap <b>Install app</b> or <b>Add to Home screen</b>. On a computer, use Chrome or Edge and click the install icon at the right of the address bar. If you don't see it, the app may already be installed on this device.</span>
          </div>
        </div>
      )}
    </div>
  );
}

/** Settings (prototype settingsPage): Profile, Password, Billing and App, with log out in the header. */
export default function SettingsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { authState: { data }, authDispatch } = useContext(GlobalContext);
  const [confirmOut, setConfirmOut] = useState(false);
  const user = data?.user;
  const tab = TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "profile";

  const logOut = async () => {
    await logoutHandler(authDispatch);
    navigate("/");
  };

  return (
    <PageFrame title="Settings" kicker="Account" intro="Your profile, password, payments and the app."
      aside={confirmOut ? (
        <div className="logout-confirm">
          <span>Log out of your dashboard?</span>
          <button className="cbtn quiet" type="button" onClick={() => setConfirmOut(false)}>Cancel</button>
          <button className="cbtn danger" type="button" onClick={logOut}>Log out</button>
        </div>
      ) : <button className="cbtn quiet logout" type="button" onClick={() => setConfirmOut(true)}><Icon name="out" size={16} /> Log out</button>}>
      <nav className="tabs" role="tablist">
        {TABS.map(([k, l]) => (
          <button key={k} className="tab" type="button" role="tab" aria-selected={tab === k} onClick={() => setParams(k === "profile" ? {} : { tab: k })}>{l}</button>
        ))}
      </nav>
      <section className="card">
        {!user ? <div className="skeleton" style={{ height: 320 }} />
          : tab === "profile" ? <Profile user={user} token={data.token} authDispatch={authDispatch} />
          : tab === "password" ? <Password authDispatch={authDispatch} />
          : tab === "app" ? <AppInstall />
          : <Billing email={user.email} />}
      </section>
    </PageFrame>
  );
}
